import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useRef, useState } from 'react';
import { CLEAR_EDITOR_COMMAND, EditorState, LexicalEditor } from 'lexical';
import { Button, FormControl, FormErrorMessage, useToast } from '@chakra-ui/react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { createCommentSchema } from 'shared/validation/schemas';
import BasicEditor from 'web2/app/components/by-type/rich-text/basic/basic-editor';
import { gql } from 'shared/__generated__/gql';
import { processLexicalState, generateInitialLexicalWithMention } from 'shared/lexical';
import { useAuth } from 'shared/misc/hooks/useAuth';
import { isInWebView, safeSendWebViewMessage } from 'shared/webview';
import { AddCommentMessages, GlobalWebViewMessages } from 'shared/webview/messages';
import { CommentItemCoreFragment, UserCoreFragment } from 'shared/__generated__/graphql';
import { useThemeColors } from 'shared/misc/hooks/useThemeColors';
import { twMerge } from 'tailwind-merge';
import PerfImage from 'shared/misc/components/util/PerfImage';
import { TWButton } from 'web2/app/components/button';

type Props = {
  targetRecId: string;
  parentCommentId?: string;
  withBorder?: boolean;
  withCancel?: boolean;
  replyToComment?: CommentItemCoreFragment;
  onCancel?: () => void;
  onComplete?: () => void;
  editMode?: boolean;
  comment?: any;
};

type Inputs = {
  contentLexicalState: EditorState | undefined | null;
};

function CommentComposer({
  targetRecId,
  parentCommentId,
  replyToComment,
  onCancel,
  onComplete,
  withBorder = true,
  withCancel = false,
  editMode = false,
  comment,
}: Props) {
  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<Inputs>({
    resolver: yupResolver(createCommentSchema),
  });

  const [editor, setEditor] = useState<LexicalEditor | null>();
  const [editorInitialized, setEditorInitialized] = useState(false);
  const [addCommentToRec, { loading }] = useMutation(ADD_COMMENT_TO_RECS);
  const [deleteCommentFromRec] = useMutation(DELETE_COMMENT_FROM_REC);
  const [editComment] = useMutation(EDIT_COMMENT);
  const toast = useToast();

  useEffect(() => {
    if (replyToComment?.originUser) {
      if (editorInitialized) setEditorInitialized(false);
      (async () => {
        try {
          const initialLexical = await generateInitialLexicalWithMention(
            replyToComment.originUser as UserCoreFragment,
            editor,
          );
          setValue('contentLexicalState', initialLexical);
          setEditorInitialized(true);
        } catch (e) {
          setEditorInitialized(true);
        }
      })();
    } else {
      setEditorInitialized(true);
    }
  }, [replyToComment?.originUser?.id]);

  const editorRef = useRef<LexicalEditor>(null);

  const onSubmit: SubmitHandler<Inputs> = async (values, event) => {
    event?.preventDefault();

    const { contentLexicalState = comment.contentLexical || null } = values;

    const { text: content, json: contentLexical } = processLexicalState(contentLexicalState);

    if (editMode) {
      if (content.length <= 0) {
        const isDeletingComment = window?.confirm('Are you sure you want to delete this comment?');
        return isDeletingComment && deleteComment();
      } else {
        return editCommentOnRec({ content, contentLexical });
      }
    }
    await addCommentToRec({
      // awaitRefetchQueries: true,
      variables: { targetRecId, parentCommentId, input: { content, contentLexical } },
      refetchQueries: [
        'getMobileRecView',
        'getRecView',
        'getMobileCommentsQuery',
        'getCommentsQuery',
      ],
      onCompleted: async () => {
        reset();
        // editor?.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
        editorRef.current?.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);

        try {
          safeSendWebViewMessage(AddCommentMessages.SUCCESS);
          safeSendWebViewMessage(GlobalWebViewMessages.HAPTIC_SUCCESS);
        } catch (e) {}
        onComplete?.();
      },
      onError: () => {
        safeSendWebViewMessage(AddCommentMessages.ERROR);
        toast({
          title: 'An error occured...',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });
      },
    });
  };

  const themeColors = useThemeColors();

  const mainColor = themeColors.main;

  const auth = useAuth();

  const editCommentOnRec = async (editedContent: { content: string; contentLexical: string }) => {
    await editComment({
      variables: {
        targetCommentId: comment.id,
        targetRecId: comment.targetRecId,
        input: { ...editedContent },
      },
      awaitRefetchQueries: true,
      refetchQueries: ['getCommentsQuery'],
      onCompleted: async () => {
        reset();
        onComplete?.();
      },
      onError: () => {
        toast({
          title: 'An error occured...',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });
      },
    });
  };

  const deleteComment = async () => {
    await deleteCommentFromRec({
      variables: { targetCommentId: comment.id, targetRecId: comment.targetRecId },
      refetchQueries: ['getRecView', 'getCommentsQuery'],
      update(cache) {
        const normalizedId = cache.identify({ id: comment.id, __typename: 'Comment' });
        cache.evict({ id: normalizedId });
        cache.gc();
      },
      onCompleted: async () => {
        toast({
          title: 'Comment deleted.',
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'top',
        });
        try {
          safeSendWebViewMessage(GlobalWebViewMessages.HAPTIC_SUCCESS);
        } catch (e) {}
      },
      onError: () => {
        toast({
          title: 'An error occured...',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });
      },
    });
  };

  if (editMode) {
    return (
      <div
        className={twMerge(
          'text-brand-main flex flex-row gap-[12px] h-full justify-start items-start',
          withBorder && 'border-[1px] border-dashed border-brand-main',
        )}
      >
        <form
          className="flex flex-col gap-[8px] w-full flex-start text-brand-main"
          onSubmit={handleSubmit(onSubmit)}
        >
          <FormControl isInvalid={!!errors.contentLexicalState} height="100%" minHeight="50px">
            {editorInitialized && (
              <Controller
                control={control}
                name="contentLexicalState"
                render={({ field: { onChange, value, ref } }) => (
                  <BasicEditor
                    includeMentions
                    includeHashTags={false}
                    placeholder="Write a comment"
                    initialEditorState={comment.contentLexical}
                    onChange={(editorState, _editor) => {
                      onChange(editorState);
                      setEditor(_editor);
                      editorRef.current = _editor;
                    }}
                    enableCmdEnter
                    shouldAutoFocus
                    onEnter={handleSubmit(onSubmit)}
                    ref={ref}
                    maxHeight="400px"
                    overflowY="scroll"
                    textStyle="brand.body"
                    color="brand.main !important"
                    border="none"
                    fontSize="16px !important"
                    placeholderProps={{
                      top: '7px !important',
                      left: '0px !important',
                      fontSize: '16px !important',
                      className: 'text-brand-main',
                    }}
                    sx={{
                      '.editor-paragraph': {
                        fontSize: '18px',
                        textStyle: 'brand.editor',
                        color: 'brand.main',
                        // p: '8px',
                        // pt: '10px',
                        // pl: '12px',
                      },
                      '.content-editable': {
                        minHeight: '50px',
                        paddingLeft: '0px !important',
                        caretColor: `${mainColor} !important`,
                      },
                    }}
                  />
                )}
              />
            )}
            {errors.contentLexicalState && (
              <FormErrorMessage>{errors.contentLexicalState?.message}</FormErrorMessage>
            )}
          </FormControl>
          <div className="flex flex-row gap-[4px] justify-end">
            {((withCancel && isInWebView()) || onCancel) && (
              <TWButton
                variant={onCancel ? 'linkNoBorder' : 'secondary'}
                onClick={() => {
                  if (onCancel) {
                    onCancel();
                  } else {
                    safeSendWebViewMessage(AddCommentMessages.CLOSE);
                  }
                }}
              >
                Cancel
              </TWButton>
            )}
            <TWButton type="submit" variant="primary" className="self-end" isLoading={loading}>
              Save
            </TWButton>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div
      className={twMerge(
        'p-[16px] flex flex-row gap-[12px] h-full justify-start items-start',
        withBorder && 'border-[1px] border-dashed border-brand-main',
      )}
    >
      <div className="flex-grow-1 flex-start h-full">
        <PerfImage
          src={
            auth?.user?.avatarPhotoSrc ||
            `https://files.pi.fyi/default-profile-photo-${
              themeColors.main === '#ffffff' || themeColors.main === 'white' ? 'white' : 'blue'
            }-small.png`
          }
          width={30}
          height={30}
          alt="image"
          quality={40}
          style={{
            minWidth: '30px',
            minHeight: '30px',
          }}
        />
      </div>
      <form className="flex flex-col text-brand-main gap-[8px] w-full flex-start" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <span className="typography-action-sm max-w-[150px] line-clamp-1 text-ellipsis">
            @{auth.user?.username}
          </span>
        </div>
        <FormControl isInvalid={!!errors.contentLexicalState} height="100%" minHeight="50px">
          {editorInitialized && (
            <Controller
              control={control}
              name="contentLexicalState"
              render={({ field: { onChange, value, ref } }) => (
                <BasicEditor
                  includeMentions
                  includeHashTags={false}
                  placeholder="Write a comment"
                  initialEditorState={value}
                  onChange={(editorState, _editor) => {
                    onChange(editorState);
                    setEditor(_editor);
                    editorRef.current = _editor;
                  }}
                  enableCmdEnter
                  onEnter={handleSubmit(onSubmit)}
                  ref={ref}
                  shouldAutoFocus={false}
                  maxHeight="400px"
                  overflowY="scroll"
                  textStyle="brand.body"
                  color="brand.main !important"
                  border="none"
                  fontSize="16px !important"
                  withBorder={false}
                  placeholderProps={{
                    className: '!top-[7px] !left-[0px] !text-[16px] text-brand-main',
                  }}
                  sx={{
                    '.editor-paragraph': {
                      fontSize: '18px',
                      textStyle: 'brand.editor',
                      color: 'brand.main',
                      // p: '8px',
                      // pt: '10px',
                      // pl: '12px',
                    },
                    '.content-editable': {
                      minHeight: '50px',
                      paddingLeft: '0px !important',
                      caretColor: `${mainColor} !important`,
                    },
                  }}
                />
              )}
            />
          )}
          {errors.contentLexicalState && (
            <FormErrorMessage>{errors.contentLexicalState?.message}</FormErrorMessage>
          )}
        </FormControl>
        <div className="flex flex-row gap-[4px] justify-end">
          {((withCancel && isInWebView()) || onCancel) && (
            <TWButton
              variant={onCancel ? 'linkNoBorder' : 'secondary'}
              onClick={() => {
                if (onCancel) {
                  onCancel();
                } else {
                  safeSendWebViewMessage(AddCommentMessages.CLOSE);
                }
              }}
            >
              Cancel
            </TWButton>
          )}
          <TWButton type="submit" variant="primary" className="self-end" isLoading={loading}>
            Reply
          </TWButton>
        </div>
      </form>
    </div>
  );
}

const ADD_COMMENT_TO_RECS = gql(/* GraphQL */ `
  mutation addCommentToRec($input: CommentInput!, $targetRecId: String!, $parentCommentId: String) {
    addCommentToRec(
      commentInput: $input
      targetRecId: $targetRecId
      parentCommentId: $parentCommentId
    ) {
      id
      ...CommentItem
    }
  }
`);

const EDIT_COMMENT = gql(/* GraphQL */ `
  mutation editComment($input: CommentInput!, $targetCommentId: String!, $targetRecId: String!) {
    editComment(
      commentInput: $input
      targetCommentId: $targetCommentId
      targetRecId: $targetRecId
    ) {
      id
      ...CommentItem
    }
  }
`);

const DELETE_COMMENT_FROM_REC = gql(/* GraphQL */ `
  mutation deleteCommentFromRec($targetCommentId: String!, $targetRecId: String!) {
    deleteCommentFromRec(targetCommentId: $targetCommentId, targetRecId: $targetRecId) {
      id
      # ...RecommendationItemComments
    }
  }
`);

export default CommentComposer;
